// src/context/CartContext.jsx - COMPLETE FIX
import React, { createContext, useContext, useState, useEffect } from 'react';
import cartService from '../services/cartService';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(false);

  /**
   * Fetch cart items
   */
  const fetchCart = async () => {
    if (!isAuthenticated) {
      setCart(null);
      setCartCount(0);
      return;
    }

    try {
      setLoading(true);
      const data = await cartService.getCart();
      setCart(data);
      
      // Calculate total item count
      const count = data.items?.reduce((total, item) => total + item.quantity, 0) || 0;
      setCartCount(count);
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCart(null);
      setCartCount(0);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch cart count only (lighter operation)
   */
  const fetchCartCount = async () => {
    if (!isAuthenticated) {
      setCartCount(0);
      return;
    }

    try {
      const count = await cartService.getCartCount();
      setCartCount(count);
    } catch (error) {
      console.error('Error fetching cart count:', error);
      setCartCount(0);
    }
  };

  /**
   * Add item to cart
   * 
   * ⭐⭐⭐ COMPLETE FIX ⭐⭐⭐
   * Updates BOTH cartCount AND cart items
   */
  const addToCart = async (item) => {
    try {
      // 1. Optimistically update count for instant feedback
      setCartCount(prevCount => prevCount + item.quantity);
      
      // 2. Call backend to add item
      const response = await cartService.addToCart(item);
      
      // 3. ⭐ FETCH FULL CART to update items list
      // This ensures cart page shows the new item
      await fetchCart();
      
      return response;
    } catch (error) {
      // Rollback count on error
      setCartCount(prevCount => prevCount - item.quantity);
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  /**
   * Update cart item quantity
   */
  const updateCartItem = async (itemId, quantity) => {
    try {
      await cartService.updateCartItem(itemId, quantity);
      
      // ⭐ Refresh cart after update
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  };

  /**
   * Remove item from cart
   */
  const removeCartItem = async (itemId) => {
    try {
      await cartService.removeCartItem(itemId);
      
      // ⭐ Refresh cart after removal
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  };

  /**
   * Clear entire cart
   */
  const clearCart = async () => {
    try {
      await cartService.clearCart();
      
      // ⭐ Reset state immediately
      setCart({ items: [] });
      setCartCount(0);
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  };

  /**
   * Fetch cart on mount and when auth status changes
   */
  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      setCart(null);
      setCartCount(0);
    }
  }, [isAuthenticated]);

  const value = {
    cart,
    cartCount,
    loading,
    addToCart,
    updateCartItem,
    removeCartItem,
    clearCart,
    fetchCart,
    fetchCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};